import { idx, isCore, type Layout } from "@empire/core-sim";

/**
 * Which road piece a tile needs, from its neighbours (board art, Sep 2026).
 *
 * The art has a straight, a corner and a dead end, each drawn with the road
 * leaving through particular edges of the diamond. Edges are named the way the
 * screen shows them: NE is up-right, SE down-right, SW down-left, NW up-left.
 * On the board that is:
 *
 *   NE → (x, y-1)   SE → (x+1, y)   SW → (x, y+1)   NW → (x-1, y)
 *
 * A road tile connects to a road neighbour, to the core (the last tile), and —
 * for the gate — to the outside of the board it sits on the edge of.
 */

export type Edge = "NE" | "SE" | "SW" | "NW";

const STEP: Record<Edge, readonly [number, number]> = {
  NE: [0, -1],
  SE: [1, 0],
  SW: [0, 1],
  NW: [-1, 0],
};

/** In the fixed order the atlas names use. */
const ORDER: readonly Edge[] = ["NE", "SE", "SW", "NW"];

const isRoadKind = (kind: string | undefined): boolean =>
  kind === "road" || kind === "mud" || kind === "barricade";

/** The edges through which this road tile's road continues. */
export function openEdges(layout: Layout, x: number, y: number): Edge[] {
  const out: Edge[] = [];
  for (const edge of ORDER) {
    const [dx, dy] = STEP[edge];
    const nx = x + dx;
    const ny = y + dy;
    const inside = nx >= 0 && ny >= 0 && nx < layout.size && ny < layout.size;
    if (!inside) continue;
    if (isCore(nx, ny) || isRoadKind(layout.tiles[idx(nx, ny, layout.size)]?.kind)) out.push(edge);
  }
  return out;
}

/**
 * The atlas frame for a road tile.
 *
 * Two open edges → a straight or a corner. One → a dead end (or, on the gate,
 * the spawn pad facing into the maze). A tile the editor has not finished
 * connecting — three neighbours mid-drag, or none — falls back to the plain
 * dirt tile rather than guessing a shape.
 */
export function roadFrame(layout: Layout, x: number, y: number): string {
  const edges = openEdges(layout, x, y);
  const isGate = layout.gate.x === x && layout.gate.y === y;
  if (isGate && edges.length === 1) return `gate_${edges[0]}`;
  if (edges.length === 1) return `road_${edges[0]}`;
  if (edges.length === 2) return `road_${edges[0]}_${edges[1]}`;
  return "dirt";
}
